"use strict";
// Copyright 2020-2022 SubQuery Pte Ltd authors & contributors
// SPDX-License-Identifier: Apache-2.0
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetworkClient = void 0;
const contract_sdk_1 = require("@subql/contract-sdk");
const ethers_1 = require("ethers");
const bignumber_1 = require("@ethersproject/bignumber");
const contractClient_1 = require("./contractClient");
const ipfsClient_1 = require("./ipfsClient");
const queryClient_1 = require("./queryClient");
const utils_1 = require("../utils");
const network_config_1 = require("@subql/network-config");
const assert_1 = __importDefault(require("assert"));
const parseEraValue_1 = require("../utils/parseEraValue");
class NetworkClient {
    constructor(_sdk, _gqlClient, ipfsUrl) {
        this._sdk = _sdk;
        this._gqlClient = _gqlClient;
        this._ipfs = new ipfsClient_1.IPFSClient(ipfsUrl !== null && ipfsUrl !== void 0 ? ipfsUrl : network_config_1.DEFAULT_IPFS_URL);
        this._contractClient = new contractClient_1.ContractClient(_sdk);
    }
    static async create(network, provider, ipfsUrl) {
        const config = network_config_1.NETWORK_CONFIGS[network];
        (0, assert_1.default)(config, `config for ${network} is missing`);
        const sdk = await contract_sdk_1.ContractSDK.create(provider !== null && provider !== void 0 ? provider : new ethers_1.providers.StaticJsonRpcProvider(config.defaultEndpoint), config.sdkOptions);
        const gqlClient = new queryClient_1.GraphqlQueryClient(config);
        return new NetworkClient(sdk, gqlClient, ipfsUrl);
    }
    async getIndexer(address) {
        var _a, _b;
        const currentEra = await this._sdk.eraManager.eraNumber();
        const leverageLimit = await this._sdk.staking.indexerLeverageLimit();
        const indexer = await this._gqlClient.getIndexer(address);
        const delegation = await this._gqlClient.getDelegation(address, address);
        if (!indexer || !delegation)
            return;
        const { controller, commission, totalStake, metadata: indexerMetadata } = indexer;
        const { amount: ownStake } = delegation;
        const metadata = { name: (_a = indexerMetadata === null || indexerMetadata === void 0 ? void 0 : indexerMetadata.name) !== null && _a !== void 0 ? _a : '', url: (_b = indexerMetadata === null || indexerMetadata === void 0 ? void 0 : indexerMetadata.url) !== null && _b !== void 0 ? _b : '' };
        const sortedTotalStake = (0, parseEraValue_1.parseRawEraValue)(totalStake, currentEra.toNumber());
        const sortedOwnStake = (0, parseEraValue_1.parseRawEraValue)(ownStake, currentEra.toNumber());
        const delegated = {
            current: sortedTotalStake.current.sub(sortedOwnStake.current),
            after: sortedTotalStake.after.sub(sortedOwnStake.after),
        };
        const capacity = {
            current: sortedOwnStake.current.mul(leverageLimit).sub(sortedTotalStake.current) ||
                bignumber_1.BigNumber.from(0),
            after: sortedOwnStake.after.mul(leverageLimit).sub(sortedTotalStake.after) || bignumber_1.BigNumber.from(0),
        };
        // Jun 2022 commission-divUnit = perMil / 100 -> 10,000
        const COMMISSION_DIV_UNIT = 10000;
        const PERCENTAGE_UNIT = 100;
        const rawCommission = (0, parseEraValue_1.parseRawEraValue)(commission, currentEra.toNumber());
        const sortedCommission = {
            current: rawCommission.current.toNumber() / (COMMISSION_DIV_UNIT * PERCENTAGE_UNIT),
            after: rawCommission.after.toNumber() / (COMMISSION_DIV_UNIT * PERCENTAGE_UNIT),
        };
        return {
            metadata,
            address,
            controller,
            commission: sortedCommission,
            totalStake: sortedTotalStake,
            ownStake: sortedOwnStake,
            delegated,
            capacity,
        };
    }
    async maxUnstakeAmount(address) {
        var _a, _b;
        const leverageLimit = await this._sdk.staking.indexerLeverageLimit();
        const minStakingAmount = await this._sdk.indexerRegistry.minimumStakingAmount();
        const indexer = await this.getIndexer(address);
        if (!indexer)
            return bignumber_1.BigNumber.from(0);
        const { totalStake, ownStake } = await indexer;
        const totalStakingAmountAfter = bignumber_1.BigNumber.from((_a = totalStake === null || totalStake === void 0 ? void 0 : totalStake.after) !== null && _a !== void 0 ? _a : 0);
        const ownStakeAfter = bignumber_1.BigNumber.from((_b = ownStake === null || ownStake === void 0 ? void 0 : ownStake.after) !== null && _b !== void 0 ? _b : 0);
        if (leverageLimit.eq(1))
            return ownStakeAfter.sub(minStakingAmount);
        const maxUnstakeAmount = (0, utils_1.min)(ownStakeAfter.sub(minStakingAmount), ownStakeAfter.mul(leverageLimit).sub(totalStakingAmountAfter).div(leverageLimit.sub(1)));
        return maxUnstakeAmount.isNegative() ? bignumber_1.BigNumber.from(0) : maxUnstakeAmount;
    }
    async getDelegating(address) {
        const currentEra = await this._sdk.eraManager.eraNumber();
        const ownDelegation = await this._gqlClient.getDelegation(address, address);
        const delegator = await this._gqlClient.getDelegator(address);
        if (!delegator)
            return bignumber_1.BigNumber.from(0);
        const eraNumber = currentEra.toNumber();
        const ownStake = ownDelegation === null || ownDelegation === void 0 ? void 0 : ownDelegation.amount;
        const { totalDelegations } = delegator;
        const sortedOwnStake = ownStake ? (0, parseEraValue_1.parseRawEraValue)(ownStake, eraNumber).after : bignumber_1.BigNumber.from(0);
        const sortedTotalDelegations = (0, parseEraValue_1.parseRawEraValue)(totalDelegations, eraNumber).after;
        return sortedTotalDelegations.sub(sortedOwnStake);
    }
    async projectMetadata(cid) {
        if (!(0, utils_1.isCID)(cid))
            throw new Error(`Invalid cid: ${cid}`);
        // get project metadata
        // cat project metadata
    }
}
exports.NetworkClient = NetworkClient;
//# sourceMappingURL=networkClient.js.map